import { useSelector } from "react-redux";
import {
  LOADING,
  LOGIN_SUCCESS,
  LOGOUT,
  SET_ACCESS_TOKEN,
  SET_CURRENT_USER_STATUS,
  SET_MESSAGE,
  SET_WEBSITE_LIST,
  SET_2FA_VERIFIED,
  SET_EDIT_ACTIVE_LANGUAGE,
  IS_FIRST_LOAD,
} from "../../Actions/Types";
import {
  UserLoginActionApi,
  getUserDetailsApi,
} from "../../Api/UserAction/LoginActionApi";
import { encryptPassword } from "../../Helpers/encryptPassword";
import { setLanguage } from "../LanguageService";
import { CompletePost2FALoginService } from "./CompletePost2FALoginService";
import { StoreProptypes } from "../../PropTypes/StoreProptypes";
import { LanguagePropTypes } from "../../PropTypes/LanguagePropTypes";
import { clearAllCacheBeforeLogin } from "../../Helpers/clearCache";

const UserLoginActionService = async (
  username: string,
  password: string,
  focusEmailInput: Function,
  dispatch: Function,
  navigate: Function,
  t: Function,
  woocommerceToken?: string,
  languageList?:LanguagePropTypes[] | null
) => {
   clearAllCacheBeforeLogin();
  dispatch({ type: LOADING, payload: true });

  const secretKey = process.env.REACT_APP_SECRET_KEY;
  const encryptedPasswordObj = await encryptPassword(
    password,
    secretKey as string
  );

  UserLoginActionApi(username, encryptedPasswordObj).then(
    async (userResponse) => {
      dispatch({ type: LOADING, payload: true });
      if (!userResponse.ok) {
        dispatch({ type: LOGOUT });
        dispatch({ type: IS_FIRST_LOAD, payload: false });
        dispatch({ type: LOADING, payload: false });
        focusEmailInput();
        const failMessage =
          userResponse.status === 500
            ? t("Login 500 Error Msg")
            : userResponse.status === 402 ? t("Delete Account Msg") : t("Login Fail Msg");
        dispatch({
          type: SET_MESSAGE,
          payload: {
            toast_message: failMessage,
            toast_type: "danger",
            onclose: null,
          },
        });

        navigate(
          woocommerceToken
            ? `/front/login?logout=true&token=${woocommerceToken}`
            : "/front/login?logout"
        );
        return;
      }

      const userDataJson = await userResponse.json();
      const userData = userDataJson.Data;
      const token = userDataJson.token;

      if (userData && token) {
        // Save token and user

        dispatch({ type: SET_ACCESS_TOKEN, payload: token });
        dispatch({ type: LOGIN_SUCCESS, payload: { user: userData } });
        dispatch({ type: LOADING, payload: false });
        dispatch({ type: SET_2FA_VERIFIED, payload: false });
        if (languageList) {
          const getCurrentLanguageType = languageList.find(d => d.code === userData.default_lang_code)
          if (getCurrentLanguageType) {
            setLanguage(getCurrentLanguageType, dispatch)
            // dispatch active language object
            dispatch({
                type: SET_EDIT_ACTIVE_LANGUAGE,
                payload: getCurrentLanguageType,
            });
          }
        }

        // Check 2FA
        if (userData.is_google2fa_enabled === 1) {
          navigate("/front/verify-twofactor");
          return;
        }
        // ✅ Call post-login flow directly if 2FA not required
        dispatch({ type: SET_2FA_VERIFIED, payload: true });
        CompletePost2FALoginService(
          token,
          userData,
          dispatch,
          navigate,
          t,
          woocommerceToken
        );

      } else {
        dispatch({ type: LOGOUT });
        dispatch({ type: IS_FIRST_LOAD, payload: false });
        dispatch({ type: LOADING, payload: false });
        focusEmailInput();

        dispatch({
          type: SET_MESSAGE,
          payload: {
            toast_message: t("Unauthorized IP address. Please log in from an approved system or network."),
            toast_type: "danger",
            onclose: null,
          },
        });

        navigate(
          woocommerceToken
            ? `/front/login?logout=true&token=${woocommerceToken}`
            : "/front/login?logout"
        );
      }
    }
  );
    // dispatch({ type: LOADING, payload: false });
};

const getUserDetailsService = async (
  token: string,
  dispatch: Function,
  isTokenSave?: boolean
): Promise<any> => {
  // Set Loading
  dispatch({
    type: LOADING,
    payload: true,
  });

  const userDetailsStatus = getUserDetailsApi(token).then(
    async (userDetails) => {
      // checks if user data is available
      if (userDetails.Data) {
        let currentUserType: number;

        // checks if its a extension user
        if (userDetails.Data.is_extension_user === 1) {
          currentUserType = 5;
        }
        // checks if its a partner user
        else if (userDetails.Data.department === 4) {
          // checks if its a multi domain partner user
          if (userDetails.Data.is_multiple_domain === 1) {
            currentUserType = 3;
          } else {
            currentUserType = 2;
          }
        }
        // check if its a normal user with multiple domains
        else if (userDetails.Data.is_multiple_domain === 2) {
          currentUserType = 4;
        } else {
          currentUserType = 1;
        }

        // dispatch user type information
        dispatch({
          type: SET_CURRENT_USER_STATUS,
          payload: {
            currentUserType: currentUserType,
            isInitUser:
              userDetails.Data.website_lists.length === 0 ? true : false,
          },
        });

        // Save User token
        if (isTokenSave) {
          dispatch({
            type: SET_ACCESS_TOKEN,
            payload: userDetails.token,
          });
        }

        // Save User Data
        dispatch({
          type: LOGIN_SUCCESS,
          payload: { user: userDetails.Data },
        });

        // Set Website List
        dispatch({
          type: SET_WEBSITE_LIST,
          payload: userDetails.Data.website_lists,
        });

        // Remove Loading
        dispatch({
          type: LOADING,
          payload: false,
        });

        return { status: 200 };
      } else {
        // Set Loading
        dispatch({
          type: LOADING,
          payload: false,
        });

        return { status: 400 };
      }
    }
  );

  return userDetailsStatus;
};

export { UserLoginActionService, getUserDetailsService };
